import { api } from './api';
import type {
  CreateTimeEntryRequest,
  UpdateTimeEntryRequest,
  TimeEntryFilters,
  TimeEntriesResponse,
  TimeEntryResponse,
  TimeEntryStatsResponse,
  RunningTimerResponse,
  UninvoicedByClientResponse,
  CreateInvoiceFromEntriesRequest,
} from '../types/timeEntry';

// Base URL for time entries
const TIME_ENTRIES_URL = '/time-entries';

/**
 * Get all time entries with filters and pagination
 */
export const getTimeEntries = async (filters?: TimeEntryFilters): Promise<TimeEntriesResponse> => {
  const params = new URLSearchParams();

  if (filters) {
    if (filters.status && filters.status !== 'all') {
      params.append('status', filters.status);
    }
    if (filters.clientId) {
      params.append('clientId', filters.clientId);
    }
    if (filters.projectId) {
      params.append('projectId', filters.projectId);
    }
    if (filters.userId) {
      params.append('userId', filters.userId);
    }
    if (filters.billable && filters.billable !== 'all') {
      params.append('billable', filters.billable);
    }
    if (filters.startDate) {
      params.append('startDate', filters.startDate);
    }
    if (filters.endDate) {
      params.append('endDate', filters.endDate);
    }
    if (filters.sortBy) {
      params.append('sortBy', filters.sortBy);
    }
    if (filters.sortOrder) {
      params.append('sortOrder', filters.sortOrder);
    }
    if (filters.page) {
      params.append('page', filters.page.toString());
    }
    if (filters.limit) {
      params.append('limit', filters.limit.toString());
    }
  }

  const queryString = params.toString();
  const url = queryString ? `${TIME_ENTRIES_URL}?${queryString}` : TIME_ENTRIES_URL;

  const response = await api.get<TimeEntriesResponse>(url);
  return response.data;
};

/**
 * Get time tracking statistics
 */
export const getTimeEntryStats = async (params?: {
  startDate?: string;
  endDate?: string;
  clientId?: string;
}): Promise<TimeEntryStatsResponse> => {
  const searchParams = new URLSearchParams();
  if (params?.startDate) searchParams.append('startDate', params.startDate);
  if (params?.endDate) searchParams.append('endDate', params.endDate);
  if (params?.clientId) searchParams.append('clientId', params.clientId);

  const queryString = searchParams.toString();
  const url = queryString ? `${TIME_ENTRIES_URL}/stats?${queryString}` : `${TIME_ENTRIES_URL}/stats`;

  const response = await api.get<TimeEntryStatsResponse>(url);
  return response.data;
};

/**
 * Get currently running timer for the user
 */
export const getRunningTimer = async (): Promise<RunningTimerResponse> => {
  const response = await api.get<RunningTimerResponse>(`${TIME_ENTRIES_URL}/running`);
  return response.data;
};

/**
 * Get a single time entry by ID
 */
export const getTimeEntry = async (id: string): Promise<TimeEntryResponse> => {
  const response = await api.get<TimeEntryResponse>(`${TIME_ENTRIES_URL}/${id}`);
  return response.data;
};

/**
 * Create a new time entry (start timer or manual entry)
 */
export const createTimeEntry = async (data: CreateTimeEntryRequest): Promise<TimeEntryResponse> => {
  const response = await api.post<TimeEntryResponse>(TIME_ENTRIES_URL, data);
  return response.data;
};

/**
 * Update a time entry
 */
export const updateTimeEntry = async (
  id: string,
  data: UpdateTimeEntryRequest
): Promise<TimeEntryResponse> => {
  const response = await api.patch<TimeEntryResponse>(`${TIME_ENTRIES_URL}/${id}`, data);
  return response.data;
};

/**
 * Stop a running timer
 */
export const stopTimer = async (id: string): Promise<TimeEntryResponse> => {
  const response = await api.post<TimeEntryResponse>(`${TIME_ENTRIES_URL}/${id}/stop`);
  return response.data;
};

/**
 * Pause a running timer
 */
export const pauseTimer = async (id: string): Promise<TimeEntryResponse> => {
  const response = await api.post<TimeEntryResponse>(`${TIME_ENTRIES_URL}/${id}/pause`);
  return response.data;
};

/**
 * Resume a paused timer
 */
export const resumeTimer = async (id: string): Promise<TimeEntryResponse> => {
  const response = await api.post<TimeEntryResponse>(`${TIME_ENTRIES_URL}/${id}/resume`);
  return response.data;
};

/**
 * Delete a time entry
 */
export const deleteTimeEntry = async (id: string): Promise<{ success: boolean }> => {
  const response = await api.delete(`${TIME_ENTRIES_URL}/${id}`);
  return response.data;
};

/**
 * Create invoice from time entries
 */
export const createInvoiceFromEntries = async (
  data: CreateInvoiceFromEntriesRequest
): Promise<{ success: boolean; data: { invoice: unknown } }> => {
  const response = await api.post(`${TIME_ENTRIES_URL}/create-invoice`, data);
  return response.data;
};

/**
 * Get uninvoiced billable time entries grouped by client
 */
export const getUninvoicedByClient = async (): Promise<UninvoicedByClientResponse> => {
  const response = await api.get<UninvoicedByClientResponse>(`${TIME_ENTRIES_URL}/uninvoiced/by-client`);
  return response.data;
};

// Export all functions as default object for convenience
export default {
  getTimeEntries,
  getTimeEntryStats,
  getRunningTimer,
  getTimeEntry,
  createTimeEntry,
  updateTimeEntry,
  stopTimer,
  pauseTimer,
  resumeTimer,
  deleteTimeEntry,
  createInvoiceFromEntries,
  getUninvoicedByClient,
};
